import React from "react";
import { Routes, Route } from "react-router-dom";
import Home from "./pages/Home";
import { CartProvider } from "./contexts/CartContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Header from "./components/Header";
import Slidebar from "./components/Slidebar";
import Footer from "./components/Footer";
import LoginPage from "./components/LoginPage";
import CheckoutPage from "./components/CheckoutPage";
import ProductDetailsPage from "./components/ProductDetailsPage";
import AccountPage from "./pages/AccountPage";
import OrdersPage from "./pages/OrdersPage";
import SettingsPage from "./pages/SettingsPage";
import ResetPassword from "./pages/ResetPassword";
import WishlistPage from "./pages/WishlistPage";
import InfoPage from "./pages/InfoPage";
import AccountExecutivePage from "./pages/AccountExecutivePage";
import ShopPage from "./pages/ShopPage";

const pageContent = {
  aboutUs: {
    path: "/about-us",
    eyebrow: "About PNGB Asher Jewels",
    title: "One of the Largest Gold Suppliers in the Philippines",
    intro:
      "PNGB Asher Jewels is not a reseller. We are one of the largest gold suppliers in the Philippines, committed to making premium gold pieces accessible through trusted service, broad branch coverage, and dependable shipping.",
    highlights: [
      { value: "28", label: "Branches Nationwide" },
      { value: "No. 1", label: "Gold Buyer in PH" },
      { value: "4", label: "Premium Gold Origins" },
      { value: "PH + Intl", label: "Shipping Coverage" },
    ],
    sections: [
      {
        heading: "Who We Are",
        paragraphs: [
          "PNGB Asher Jewels offers a wide range of premium gold, including Saudi gold, Japan gold, Italy gold, and Turkish gold.",
          "We serve customers nationwide and internationally, making our gold collections available wherever you are.",
        ],
      },
      {
        heading: "What We Offer",
        paragraphs: [
          "With 28 branches across the country, located in major malls such as Robinsons and SM, we are committed to serving customers conveniently and consistently.",
          "We are also the No. 1 gold buyer here in the Philippines. We purchase scrap gold, offer trade-in options, and can remake your gold into new, beautifully crafted pieces.",
        ],
        list: [
          "Premium gold selections across multiple gold origins",
          "Nationwide and international shipping",
          "28 branches in key mall locations",
          "Scrap gold buying, trade-in, and gold remaking services",
        ],
      },
    ],
    contactCard: {
      title: "PNGB Commitment",
      lines: [
        "One of the largest gold suppliers in the Philippines",
        "No. 1 gold buyer in the country",
        "PNGB (Philippine's No. 1 Gold Buyer)",
      ],
    },
  },
  terms: {
    path: "/terms",
    eyebrow: "Store Policy",
    title: "Terms & Conditions",
    intro:
      "These terms govern the use of the Asher Jewels PH online store, including browsing, account registration, and order placement.",
    sections: [
      {
        heading: "Orders and Pricing",
        paragraphs: [
          "All orders are subject to stock confirmation and payment verification. Prices displayed on the website may be updated without prior notice, but confirmed orders will follow the final validated checkout amount.",
          "Orders may be declined or cancelled if pricing, stock, or customer information cannot be properly verified.",
        ],
      },
      {
        heading: "Account Responsibility",
        paragraphs: [
          "Customers are responsible for maintaining the confidentiality of their login credentials and for ensuring that account details are accurate and current.",
          "Any activity completed under a customer account is considered authorized unless promptly reported to customer support.",
        ],
      },
      {
        heading: "Use of the Website",
        paragraphs: [
          "Users agree not to misuse the website, interfere with its operation, or submit fraudulent transactions or false account information.",
        ],
        list: [
          "Do not attempt unauthorized access to protected areas",
          "Do not use the site for fraudulent or abusive transactions",
          "Do not copy or redistribute product content without permission",
        ],
      },
    ],
  },
  privacy: {
    path: "/privacy",
    eyebrow: "Data Protection",
    title: "Privacy Policy",
    intro:
      "We value your privacy and only collect the information needed to process orders, support your account, and improve the customer experience.",
    sections: [
      {
        heading: "Information We Collect",
        paragraphs: [
          "We may collect your name, contact number, email address, delivery details, and account activity related to purchases and support requests.",
        ],
      },
      {
        heading: "How We Use Your Information",
        paragraphs: [
          "Customer information is used to process orders, coordinate shipping, send purchase-related updates, and assist with account or service concerns.",
        ],
        list: [
          "Order fulfillment and delivery coordination",
          "Account verification and support",
          "Purchase history and customer service records",
          "Store improvement and issue resolution",
        ],
      },
      {
        heading: "Data Security",
        paragraphs: [
          "We take reasonable steps to protect your information through controlled system access and secure handling of customer account data.",
          "If you believe your account has been compromised, please contact us immediately so we can assist you.",
        ],
      },
    ],
  },
  returns: {
    path: "/returns",
    eyebrow: "After-Sales Support",
    title: "Returns & Exchanges",
    intro:
      "We review return and exchange requests carefully to ensure fair handling while protecting customers from avoidable issues related to damaged, incorrect, or ineligible items.",
    sections: [
      {
        heading: "Eligible Cases",
        paragraphs: [
          "Returns or exchanges may be reviewed for incorrect items, confirmed defects, or issues reported promptly after receipt.",
        ],
        list: [
          "Wrong item received",
          "Visible product defect upon delivery",
          "Order issue supported by photos and purchase details",
        ],
      },
      {
        heading: "Non-Eligible Cases",
        paragraphs: [
          "Requests may be declined for items damaged through misuse, normal wear, or customer handling after delivery.",
        ],
        list: [
          "Damage from improper use or storage",
          "Requests without proof of purchase",
          "Change-of-mind returns for customized or special-order items",
        ],
      },
      {
        heading: "Request Process",
        paragraphs: [
          "Please contact customer service with your order code, item details, and supporting photos so our team can review the concern and advise the next step.",
        ],
      },
    ],
  },
  shipping: {
    path: "/shipping",
    eyebrow: "Delivery Information",
    title: "Shipping & Delivery",
    intro:
      "Orders are prepared after payment and stock confirmation. For available items, standard shipping lead time is usually 1 to 3 days within the Philippines.",
    sections: [
      {
        heading: "Processing Timeline",
        paragraphs: [
          "Orders are validated for stock, payment, and delivery information before release for shipping. Processing may take longer during peak periods or for orders requiring additional verification.",
        ],
      },
      {
        heading: "Delivery Lead Time",
        paragraphs: [
          "For ready and confirmed items, shipping lead time is typically 1 to 3 days. Delivery timing may still vary depending on destination, courier conditions, weather, and operational factors.",
        ],
      },
      {
        heading: "Customer Reminders",
        paragraphs: [
          "Please ensure your shipping details and contact number are complete and accurate before finalizing checkout to help avoid dispatch delays.",
        ],
        list: [
          "Double-check recipient name and mobile number",
          "Provide complete address details including landmarks when needed",
          "Monitor your order history for status changes",
        ],
      },
    ],
  },
  contact: {
    path: "/contact",
    eyebrow: "Customer Support",
    title: "Contact Us",
    intro:
      "If you need help with an order, account update, return concern, or product inquiry, our customer support team is ready to assist.",
    sections: [
      {
        heading: "How to Reach Us",
        paragraphs: [
          "For faster support, please prepare your order code or the product details related to your concern before contacting us.",
        ],
        list: [
          "Mobile: +63 970 757 8888",
          "Email: pngbcustomerservice@gmail.com",
          "Address: Blk 3 Lot 3 Orchard Avenue St., The Orchard Residence, Salitran 1, Dasmariñas, Cavite, Philippines 4114",
        ],
      },
      {
        heading: "Best Topics to Send",
        paragraphs: [
          "We can help with product inquiries, order follow-ups, payment confirmation concerns, account assistance, and after-sales support.",
        ],
      },
    ],
    contactCard: {
      title: "Support Tip",
      lines: [
        "Include your name and order code",
        "Attach product photos when reporting an issue",
        "For shipping concerns, confirm the full delivery address",
      ],
    },
  },
};

function AppWithCart() {
  const { user } = useAuth();

  return (
    <CartProvider user={user}>
      <Header />
      <Slidebar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/product/:id" element={<ProductDetailsPage />} />
        <Route path="/account-executive/:id" element={<AccountExecutivePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/about-us" element={<InfoPage {...pageContent.aboutUs} />} />
        <Route path="/terms" element={<InfoPage {...pageContent.terms} />} />
        <Route path="/privacy" element={<InfoPage {...pageContent.privacy} />} />
        <Route path="/returns" element={<InfoPage {...pageContent.returns} />} />
        <Route path="/shipping" element={<InfoPage {...pageContent.shipping} />} />
        <Route path="/contact" element={<InfoPage {...pageContent.contact} />} />
      </Routes>
      <Footer />
    </CartProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppWithCart />
    </AuthProvider>
  );
}

export default App;
